import { formatMoney, parseMoney } from '@ifixit/helpers';
import { LocaleCode } from '@ifixit/i18n';
import { ProductSearchResultItem } from '../types';

function getHitPriceTier(
   hit: ProductSearchResultItem,
   priceTier: string | null
): { min: number; max: number } | null {
   if (!hit.price_tiers || !priceTier || !hit.price_tiers[priceTier]) {
      return null;
   }
   return hit.price_tiers[priceTier];
}

function getHitGroupPricing(hit: ProductSearchResultItem): { min: number; max: number } | null {
   if (hit.group_min_price === undefined || hit.group_max_price === undefined) {
      return null;
   }
   return {
      min: convertCentsToDecimal(hit.group_min_price),
      max: convertCentsToDecimal(hit.group_max_price),
   };
}

function convertCentsToDecimal(cents: number): number {
   return cents / 100;
}

function formatPriceRange(
   minPrice: number,
   maxPrice: number,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): { formattedPrice: string; isRange: boolean } {
   if (minPrice === maxPrice) {
      return {
         formattedPrice: formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix),
         isRange: false,
      };
   }
   if (minPrice === 0) {
      return {
         formattedPrice: formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix),
         isRange: false,
      };
   }
   const formattedMin = formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix);
   const formattedMax = formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix);
   return {
      formattedPrice: `${formattedMin} - ${formattedMax}`,
      isRange: true,
   };
}
type GetPriceResultProps = {
   price_float: number;
   compare_at_price: number | undefined;
   currency: string;
   localeCodeOrUrlPrefix: LocaleCode | string;
   price_range?: {
      groupPricing: { min: number; max: number };
      tierPricing?: { min: number; max: number };
   };
};

type PriceResult = {
   formattedPrice: string;
   isDiscounted: boolean;
   formattedOriginalPrice: string;
   isRange: boolean;
   discountPercent: number;
};

function getPriceResult({
   price_float,
   price_range,
   compare_at_price,
   currency,
   localeCodeOrUrlPrefix,
}: GetPriceResultProps): PriceResult {
   const baseCompareAt = compare_at_price ?? price_float;
   const formattedOriginalPrice =
      baseCompareAt === undefined
         ? ''
         : formatMoney(parseMoney(baseCompareAt, currency), localeCodeOrUrlPrefix);

   if (price_range) {
      const { tierPricing, groupPricing } = price_range;
      const preferredRange = tierPricing || groupPricing;
      const { formattedPrice: formattedOriginalPrice } = formatPriceRange(
         groupPricing.min,
         groupPricing.max,
         currency,
         localeCodeOrUrlPrefix
      );

      const { formattedPrice, isRange } = formatPriceRange(
         preferredRange.min,
         preferredRange.max,
         currency,
         localeCodeOrUrlPrefix
      );

      const comparePrice = Math.max(compare_at_price || 0, groupPricing.min);

      return {
         formattedPrice,
         isDiscounted: tierPricing ? tierPricing.min < comparePrice : comparePrice > price_float,
         formattedOriginalPrice: compare_at_price
            ? formatMoney(parseMoney(compare_at_price, currency), localeCodeOrUrlPrefix)
            : formattedOriginalPrice,
         isRange,
         discountPercent: Math.round(((comparePrice - preferredRange.min) / comparePrice) * 100),
      };
   }

   return {
      formattedPrice: formatMoney(parseMoney(price_float, currency), localeCodeOrUrlPrefix),
      isDiscounted: baseCompareAt > price_float,
      formattedOriginalPrice,
      isRange: false,
      discountPercent: Math.round(((baseCompareAt - price_float) / baseCompareAt) * 100),
   };
}

export function determineProductPrice(
   hit: ProductSearchResultItem,
   priceTier: string | null,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): PriceResult {
   const tierPricing = getHitPriceTier(hit, priceTier);
   const groupPricing = getHitGroupPricing(hit);
   const sharedProps = {
      price_float: hit.price_float!,
      compare_at_price: hit.compare_at_price,
      currency,
      localeCodeOrUrlPrefix,
   };

   if (tierPricing && groupPricing) {
      return getPriceResult({
         ...sharedProps,
         price_range: { tierPricing, groupPricing },
      });
   }

   if (groupPricing) {
      return getPriceResult({
         ...sharedProps,
         price_range: { groupPricing },
      });
   }

   return getPriceResult(sharedProps);
}
